// Mobile input: virtual joystick (left), device-orientation look (gyro,
// compass-true when the phone has a magnetometer), drag-look + pinch zoom on
// the view, the zoom slider, the 🔒 view lock, FIRE (gun in hand only), GPS
// placement and the 📍 follow toggle. One view (APP_PLAYER.md §5): the zoom
// itself, the tilt-to-map switch and the HUD live in main.js / viewzoom.js.
// Exports MobileControls — same interface shape as FlatControls so main.js
// can branch on mode without duplicating the tick body.
import * as THREE from 'three';
import { t } from './i18n.js';
import { mountHints } from './keyhints.js';
import './i18n/player.js';

// --------------------------------------------------------- GPS → game coords
// meta.json carries crs (EPSG:2056 = LV95 for Swiss worlds), bbox {e0,n0,e1,n1},
// and game_origin {e0, n1}. Game coords: x = E − e0, z = n1 − N, y = elev.
// For small regions (~15 km) a linearised WGS84 → LV95 is accurate to ~1 m —
// far inside GPS noise. Full proj4 would add 30 KB for no perceptible gain.
// The approximate formulas below are the Swiss Federal Office of Topography's
// own published polynomial (accurate to ~1 m over all of Switzerland).

function wgs84ToLv95(lat, lng) {
  const phi  = (lat * 3600 - 169028.66) / 10000;
  const lam  = (lng * 3600 -  26782.5)  / 10000;
  const E = 2600072.37
    + 211455.93 * lam
    -  10938.51 * lam * phi
    -      0.36 * lam * phi * phi
    -     44.54 * lam * lam * lam;
  const N = 1200147.07
    + 308807.95 * phi
    +   3745.25 * lam * lam
    +     76.63 * phi * phi
    -    194.56 * lam * lam * phi
    +    119.79 * phi * phi * phi;
  return { E, N };
}

export function gpsToGame(lat, lng, meta) {
  if (meta.crs && meta.crs !== 'EPSG:2056') return { x: 0, z: 0, inBounds: false, unsupported: true };
  const lv = wgs84ToLv95(lat, lng);
  const o = meta.game_origin || meta.bbox;
  const e0 = o.e0, n1 = o.n1 !== undefined ? o.n1 : meta.bbox.n1;
  const x = lv.E - e0;
  const z = n1 - lv.N;
  const bb = meta.bbox;
  const W = bb.e1 - bb.e0, H = bb.n1 - bb.n0;
  const inBounds = x >= 0 && x <= W && z >= 0 && z <= H;
  return { x, z, inBounds };
}

// Compass → game-north correction, degrees to ADD to a magnetic heading to
// get a grid heading in this world: magnetic declination (magnetic north is
// east of true north over Switzerland, ~+3.3° in 2026 — WMM) minus the LV95
// grid convergence (grid north vs true north, positive east of the Bern
// meridian: ~0.9° at Zürich, ~1.7° at St. Moritz). Both are well under a
// phone compass's ±5–15°, but the prime directive is geo-accuracy, so the
// systematic part is corrected. LV95 only (the same limit as gpsToGame).
export function headingCorrectionDeg(meta) {
  if (meta.crs && meta.crs !== 'EPSG:2056') return 0;
  const bb = meta.bbox;
  const E = (bb.e0 + bb.e1) / 2, N = (bb.n0 + bb.n1) / 2;
  const lat = 46.9524 + (N - 1200000) / 111200;             // Bern origin, m → °
  const dLon = (E - 2600000) / (111320 * Math.cos(lat * Math.PI / 180));
  const convergence = dLon * Math.sin(lat * Math.PI / 180);
  return 3.3 - convergence;
}

// ----------------------------------------------------- Permissions-Policy
// Does THIS page's own Permissions-Policy header allow a powerful feature?
// → true | false | null, where null means "this browser cannot tell" and must
// never be read as a verdict: a server in front of the game can ship
// `permissions-policy: geolocation=(), gyroscope=(), microphone=()`, and an
// empty allowlist `()` switches the feature off for EVERY origin including the
// page itself — no prompt, no setting, nothing the person holding the phone can
// do about it. Detecting that directly is the only way to stop the guidance
// cards from sending users to a padlock menu that cannot help.
// Lives here (not main.js) because mobile.js must not import from main.js —
// same direction as headingCorrectionDeg, which main.js imports from here.
export function policyAllows(feature) {
  try {
    // `document.permissionsPolicy` is the current spelling, `featurePolicy` the
    // older Chromium one; Safari has neither, hence the null verdict.
    const pp = document.permissionsPolicy || document.featurePolicy;
    if (!pp || typeof pp.allowsFeature !== 'function') return null;
    // A feature name the browser does not know returns false from
    // allowsFeature — indistinguishable from a real block. features() lists
    // the names it does know, so an unknown one reports "cannot tell" rather
    // than inventing a header that was never sent.
    if (typeof pp.features === 'function' && !pp.features().includes(feature)) return null;
    return !!pp.allowsFeature(feature);
  } catch { return null; }
}

// --------------------------------------------------------- touch helpers
const TAU = Math.PI * 2;
const _STICK_DZ = 0.15;       // joystick deadzone, shared by every reader

// shortest signed angle to a difference of degrees (−180..180]. Every compass
// fusion step works on differences, and a raw subtraction across the 0/360
// seam (359° − 1°) would otherwise read as a 358° error and snap the view.
function wrap180(d) {
  return ((d + 180) % 360 + 360) % 360 - 180;
}

function dist(a, b) {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// --------------------------------------------------------- control overlay DOM
const DOUBLE_TAP_MS = 320;   // the zoom slider: double tap = back to 1×

// the jump button: a rocket with swept wings (an icon only — a tap is a jump)
const ROCKET_SVG = `<svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"><path d="M16 3c4 3 6 8 6 14l-2.5 4h-7L10 17c0-6 2-11 6-14z"/><circle cx="16" cy="12" r="2.2"/><path d="M10 15l-5 6 5-1.5M22 15l5 6-5-1.5"/><path d="M13.5 24.5c.6 2 1.4 3.4 2.5 4.5 1.1-1.1 1.9-2.5 2.5-4.5" stroke="#fbbf24"/></svg>`;

function createOverlay() {
  const el = document.createElement('div');
  el.id = 'mobile-controls';
  el.innerHTML = `
    <canvas id="mob-stick" width="200" height="200"></canvas>
    <div id="mob-right">
      <button id="mob-fire">${t('player.mob.fire')}</button>
      <button id="mob-weapon">⟳</button>
      <button id="mob-jump" aria-label="${t('player.mob.jump')}">${ROCKET_SVG}</button>
      <button id="mob-insp" class="dim" aria-label="${t('player.mob.inspect')}"><span id="mob-insp-ic">◎</span></button>
    </div>
    <!-- GPS: top, left of the round minimap (#pmap, top right) -->
    <button id="mob-gps" aria-label="${t('player.mob.gpsLabel')}"><span class="g-ic">📍</span><small>${t('player.mob.gpsLabel')}</small></button>
    <div id="mob-zoom">
      <span class="mz-cap">＋</span>
      <div id="mob-zoom-track"><div id="mob-zoom-fill"></div><div id="mob-zoom-thumb"></div></div>
      <span class="mz-cap">－</span>
      <span id="mob-zoom-val">1.0×</span>
    </div>
    <!-- the reticle's centre dot + what it is on (setInspect); the ◎ button
         bottom right opens the card (consumeInspectTap). Both shown by setUi only -->
    <div id="mob-insp-dot" aria-hidden="true"></div>
    <div id="mob-insp-lbl" aria-hidden="true"></div>
    <!-- the key-hint line (main.js setKeyHints → keyhints.js mountHints): bottom
         centre between the stick and the zoom slider, one line, pages cycle -->
    <div id="mob-keys" class="kh" aria-hidden="true"></div>
    <!-- GPS guidance card (showGpsHelp): the landing page is unreachable in
         fullscreen, so a failed location request has to explain itself here -->
    <div id="mob-gpshelp">
      <button id="mob-gh-x" aria-label="${t('player.mob.close')}">×</button>
      <div id="mob-gh-title"></div>
      <div id="mob-gh-note"></div>
      <ol id="mob-gh-steps"></ol>
      <div id="mob-gh-tech"></div>
      <button id="mob-gh-retry">${t('player.mob.retry')}</button>
    </div>`;
  el.style.cssText = `position:fixed;inset:0;z-index:6;pointer-events:none;display:none;`;

  const css = document.createElement('style');
  css.textContent = `
    #mob-stick {
      position:fixed; left:calc(16px + env(safe-area-inset-left, 0px));
      bottom:calc(16px + env(safe-area-inset-bottom, 0px));
      width:160px; height:160px; pointer-events:auto;
      touch-action:none; opacity:0.55;
      transition:opacity .18s;
    }
    #mob-right {
      position:fixed; right:calc(12px + env(safe-area-inset-right, 0px));
      bottom:calc(16px + env(safe-area-inset-bottom, 0px));
      display:flex; flex-direction:column; gap:10px;
      align-items:center; pointer-events:auto;
    }
    #mob-right button {
      width:64px; height:64px; border-radius:50%;
      border:2px solid rgba(255,255,255,.45);
      background:rgba(255,255,255,.12);
      color:#fff; font:bold 13px/1 system-ui;
      touch-action:none; user-select:none;
      -webkit-user-select:none;
      backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px);
    }
    #mob-fire { width:72px; height:72px; font-size:15px;
      border-color:rgba(248,113,113,.7); background:rgba(248,113,113,.18); }
    #mob-fire.held { background:rgba(248,113,113,.45); }
    #mob-weapon { font-size:22px; width:48px; height:48px; }
    #mob-jump { border-color:rgba(74,222,128,.55); color:#bbf7d0;
      background:rgba(74,222,128,.14); width:56px; height:56px; padding:0;
      display:flex; align-items:center; justify-content:center; }
    /* the inspect button (bottom of the thumb column): dim = the centre dot is
       on nothing specific, lit = it is on something (icon + label) */
    #mob-insp { width:64px; height:64px; padding:0; font-size:26px; line-height:1;
      display:flex; align-items:center; justify-content:center;
      border-color:rgba(103,232,249,.35); background:rgba(103,232,249,.06); color:rgba(255,255,255,.4); }
    #mob-insp.lit { border-color:rgba(103,232,249,.95); background:rgba(103,232,249,.32); color:#fff;
      box-shadow:0 0 14px rgba(103,232,249,.45); }
    #mob-insp-dot { position:fixed; left:50%; top:50%; width:6px; height:6px; margin:-3px 0 0 -3px;
      border-radius:50%; background:rgba(255,255,255,.45); pointer-events:none; z-index:6; }
    #mob-insp-dot.lit { background:rgb(103,232,249); box-shadow:0 0 8px rgba(103,232,249,.9); }
    #mob-insp-lbl { position:fixed; left:50%; top:calc(50% + 16px); transform:translateX(-50%);
      max-width:60vw; padding:3px 10px; border-radius:12px; white-space:nowrap;
      overflow:hidden; text-overflow:ellipsis; pointer-events:none; z-index:6;
      border:1px solid rgba(103,232,249,.6); background:rgba(9,15,25,.78); color:#e6fbff;
      font:600 12px/1.3 system-ui, sans-serif; }
    /* GPS follow: off = dim, .active = following (label shows ±accuracy),
       .err = the fix failed (label says why, short) */
    #mob-gps { position:fixed; pointer-events:auto; z-index:6;
      top:calc(12px + env(safe-area-inset-top, 0px));
      right:calc(134px + env(safe-area-inset-right, 0px));   /* left of the 112 px minimap */
      width:44px; height:44px; padding:0; border-radius:50%; color:#fff;
      font:bold 13px/1 system-ui; touch-action:none; user-select:none; -webkit-user-select:none;
      backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px);
      display:flex; flex-direction:column; align-items:center; justify-content:center; line-height:1;
      border:2px solid rgba(250,204,21,.4); background:rgba(250,204,21,.08); }
    #mob-gps .g-ic { font-size:15px; opacity:.5; filter:grayscale(1); }
    #mob-gps small { font-size:8px; letter-spacing:.04em; opacity:.7; }
    #mob-gps.active { background:rgba(250,204,21,.34); border-color:rgba(250,204,21,.95); }
    #mob-gps.active .g-ic { opacity:1; filter:none; }
    #mob-gps.active small { opacity:.95; }
    #mob-gps.err { border-color:rgba(248,113,113,.7); background:rgba(248,113,113,.18); }
    /* zoom slider — always there, bottom right beside the button column
       (thumb reach; clear of the minimap top right). Log-mapped to the view
       zoom Z; a double tap goes back to 1× */
    #mob-zoom {
      position:fixed; right:calc(78px + env(safe-area-inset-right, 0px));
      bottom:calc(16px + env(safe-area-inset-bottom, 0px));
      display:flex; flex-direction:column; align-items:center; gap:6px;
      pointer-events:auto; touch-action:none; user-select:none;
      -webkit-user-select:none; padding:8px 12px;
    }
    #mob-zoom .mz-cap { color:rgba(170,255,215,.8); font:bold 16px/1 system-ui; }
    /* live magnification readout (the phone's MAG) */
    #mob-zoom-val {
      color:rgba(103,232,249,.95); text-align:center; min-width:46px;
      font:bold 12px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
      text-shadow:0 1px 4px rgba(0,0,0,.8);
    }
    #mob-zoom-track {
      position:relative; width:12px; height:min(30vh, 220px);
      border-radius:8px; border:1px solid rgba(103,232,249,.5);
      background:rgba(0,12,10,.55);
    }
    #mob-zoom-fill {
      position:absolute; left:0; right:0; bottom:0; height:0%;
      border-radius:8px; background:rgba(103,232,249,.35);
    }
    #mob-zoom-thumb {
      position:absolute; left:50%; bottom:0%;
      width:34px; height:34px; transform:translate(-50%, 50%);
      border-radius:50%; border:2px solid rgba(103,232,249,.85);
      background:rgba(10,25,30,.85);
      box-shadow:0 0 12px rgba(103,232,249,.35);
    }
    /* the view lock (body-level, over the Map too): 🔒 freezes the view
       where it points; without orientation data it is the Map button */
    #mob-lock {
      position:fixed; left:calc(12px + env(safe-area-inset-left, 0px));
      top:calc(12px + env(safe-area-inset-top, 0px));
      width:44px; height:44px; border-radius:50%; z-index:9;
      display:none; align-items:center; justify-content:center;
      border:2px solid rgba(255,255,255,.45); background:rgba(12,16,24,.45);
      color:#fff; font:20px/1 system-ui; padding:0;
      touch-action:none; user-select:none; -webkit-user-select:none;
      backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px);
    }
    #mob-lock.on { border-color:rgba(250,204,21,.85); background:rgba(250,204,21,.28); }
    /* GPS guidance card — sits over the canvas, left of the button column so
       the 📍 button it talks about stays reachable. Scrolls: a landscape phone
       has ~300 px of height and the steps must never be cut off. */
    #mob-gpshelp {
      position:fixed; display:none; pointer-events:auto;
      left:calc(12px + env(safe-area-inset-left, 0px));
      top:calc(10px + env(safe-area-inset-top, 0px));
      width:min(420px, calc(100vw - 150px)); max-height:min(72vh, 340px);
      max-height:min(72dvh, 340px);
      overflow-y:auto; -webkit-overflow-scrolling:touch;
      padding:12px 14px 12px; border-radius:12px;
      border:1px solid rgba(251,191,36,.5); background:rgba(9,15,25,.94);
      box-shadow:0 0 26px rgba(251,191,36,.14);
      color:#c3cfe2; font:13px/1.5 system-ui, -apple-system, sans-serif;
      backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px);
    }
    #mob-gpshelp.show { display:block; }
    #mob-gh-title { font-size:13.5px; font-weight:700; color:#fde68a;
      line-height:1.4; margin:0 22px 6px 0; }
    #mob-gh-note { font-size:12px; color:#fca5a5; margin-bottom:6px; }
    #mob-gh-note:empty { display:none; }
    #mob-gh-steps { margin:0 0 8px; padding-left:18px; font-size:12.5px; }
    #mob-gh-steps li { margin-bottom:5px; }
    #mob-gh-steps li::marker { color:#fbbf24; font-weight:700; }
    #mob-gh-tech { font:10px/1.45 ui-monospace, monospace; color:#6f85a3;
      word-break:break-all; margin-bottom:9px; }
    #mob-gpshelp #mob-gh-x {
      position:absolute; top:6px; right:6px; width:30px; height:30px;
      border-radius:50%; border:1px solid rgba(255,255,255,.35);
      background:rgba(255,255,255,.10); color:#fff; font:bold 17px/1 system-ui;
      touch-action:none; -webkit-user-select:none; user-select:none;
    }
    #mob-gpshelp #mob-gh-retry {
      padding:8px 18px; border-radius:9px; font:bold 13px/1 system-ui;
      border:1px solid rgba(251,191,36,.6); background:rgba(251,191,36,.18);
      color:#fde68a; touch-action:none; -webkit-user-select:none; user-select:none;
    }
    #mob-keys { position:fixed; left:50%; transform:translateX(-50%);
      bottom:calc(14px + env(safe-area-inset-bottom, 0px)); max-width:calc(100vw - 330px);
      display:grid; justify-items:center; white-space:nowrap; overflow:hidden;
      padding:3px 10px; border-radius:10px; pointer-events:none; background:rgba(12,14,20,.5);
      color:rgba(220,226,236,.85); font:500 11.5px/1.35 system-ui, sans-serif;
      text-shadow:0 1px 3px rgba(0,0,0,.7); }
    @media (orientation:portrait) { #mob-keys { display:none; } }
    body.beam-flight #mob-keys { display:none; }
    @media (orientation:landscape) {
      #mob-stick { width:140px; height:140px; left:12px; bottom:12px; }
      #mob-right { bottom:12px; right:10px; gap:8px; }
      #mob-right button { width:56px; height:56px; font-size:12px; }
      #mob-fire { width:64px; height:64px; font-size:14px; }
      #mob-weapon { width:42px; height:42px; font-size:18px; }
      #mob-jump { width:48px; height:48px; }
      #mob-insp { width:56px; height:56px; }
    }
    /* portrait in the world (iPhone Safari cannot lock landscape): a "turn
       your phone" card over the view instead of the squeezed layout; turning
       the phone takes it away, "play in portrait" dismisses it for the page */
    #mob-rotate {
      position:fixed; inset:0; z-index:10; display:none;
      flex-direction:column; align-items:center; justify-content:center; gap:14px;
      padding:24px; box-sizing:border-box; text-align:center;
      background:rgba(6,10,16,.94); color:#e8ecf4;
      font:15px/1.45 system-ui, -apple-system, sans-serif;
    }
    #mob-rotate svg { width:120px; height:120px; color:#86efac; }
    #mob-rotate b { font-size:18px; }
    #mob-rotate span { color:#9fb3d1; font-size:13.5px; }
    #mob-rotate button {
      margin-top:6px; padding:9px 18px; border-radius:10px; cursor:pointer;
      border:1px solid rgba(255,255,255,.3); background:rgba(255,255,255,.08); color:#c3cfe2;
      font:600 13.5px/1 system-ui, sans-serif; touch-action:manipulation;
    }
    @media (orientation:portrait) {
      body.mob #mob-rotate:not(.dismissed) { display:flex; }
    }
  `;
  document.head.appendChild(css);
  document.body.appendChild(el);

  const rot = document.createElement('div');
  rot.id = 'mob-rotate';
  rot.setAttribute('role', 'alert');
  rot.innerHTML = `<svg viewBox="0 0 120 120" aria-hidden="true" fill="none" stroke="currentColor"
      stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <rect x="44" y="18" width="32" height="56" rx="6" stroke-opacity=".45"/>
      <rect x="22" y="62" width="76" height="40" rx="7"/><circle cx="34" cy="82" r="2.5" fill="currentColor" stroke="none"/>
      <path d="M86 26a34 34 0 0 1 14 30M94 54l6 4 4-7"/></svg>
    <b>${t('player.mob.rotate.title')}</b>
    <span>${t('player.mob.rotate.body')}</span>
    <button type="button">${t('player.mob.rotate.portrait')}</button>`;
  rot.querySelector('button').addEventListener('click', () => rot.classList.add('dismissed'));
  document.body.appendChild(rot);

  // the lock is body-level (appended after #pmap-full, same z-index → on top
  // of it): it has to stay reachable over the Map, where it is the way back
  const lock = document.createElement('button');
  lock.id = 'mob-lock';
  lock.setAttribute('aria-label', t('player.mob.lockView'));
  lock.textContent = '🔓';
  document.body.appendChild(lock);
  return { el, lock };
}

// ------------------------------------------------------------- virtual stick
function drawStick(ctx, cx, cy, r, dx, dy) {
  const W = ctx.canvas.width, H = ctx.canvas.height;
  ctx.clearRect(0, 0, W, H);
  // outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.strokeStyle = 'rgba(255,255,255,.35)';
  ctx.lineWidth = 2;
  ctx.stroke();
  // deadzone cross
  const dz = r * 0.15;
  ctx.strokeStyle = 'rgba(255,255,255,.12)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - dz, cy); ctx.lineTo(cx + dz, cy);
  ctx.moveTo(cx, cy - dz); ctx.lineTo(cx, cy + dz);
  ctx.stroke();
  // thumb
  ctx.beginPath();
  ctx.arc(cx + dx * r, cy + dy * r, r * 0.32, 0, TAU);
  ctx.fillStyle = 'rgba(255,255,255,.45)';
  ctx.fill();
}

// ---- quaternion helpers (device orientation → Three.js camera)
// Same conversion as WebXR's phone VR path: (alpha, beta, gamma) →
// quaternion with a −90° X pre-rotation so "device upright" = looking
// at the horizon, and a Z rotation for screen orientation.
const _D2R = Math.PI / 180;
const _euler = new THREE.Euler();
const _dq = new THREE.Quaternion();
const _q0 = new THREE.Quaternion();
const _Q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));
const _zee = new THREE.Vector3(0, 0, 1);
const _yUp = new THREE.Vector3(0, 1, 0);
const _eYaw = new THREE.Euler();
const _yawOf = (q) => _eYaw.setFromQuaternion(q, 'YXZ').y;

// -------------------------------------------------------- MobileControls class
export class MobileControls {
  // `lookEl` = the render canvas: a one-finger drag on it (outside the stick
  // and buttons, which sit above it) looks around — the fallback when the
  // phone delivers no orientation events, and a yaw nudge when it does
  constructor(lookEl) {
    this.fireHeld = false;
    this.locked = true;     // always "locked" so retractWeapon treats it like flat
    // 🔒: the view stays where it points (gyro + drag + stick yaw ignored,
    // the tilt switch frozen); unlocking continues from that heading
    this.viewLock = false;
    this._rebase = false;
    this.lookScale = 1;     // main.js: fov / fov0 (drag-look follows the zoom)

    this._stickX = 0;       // normalised −1..1
    this._stickY = 0;
    this._jumpQueued = false;
    this._weaponNext = false;
    this._inspTap = false;      // the ◎ inspect button (consumeInspectTap)
    this._inspHint = '';        // last rendered inspect hint (setInspect, per-frame cheapness)
    this._pinch = 1;            // accumulated pinch scale (consumePinch)
    this._zoomReset = false;    // slider double tap (consumeZoomReset)
    this._mapToggle = false;    // 🗺 (the lock button without orientation data)

    // gyro — raw sensor values, applied as a quaternion each frame.
    // gyroState: 'waiting' (listener armed, no event yet) | 'live' |
    // 'permission' (iOS: must ask on a gesture) | 'denied' | 'unsupported' |
    // 'policy' (this site's Permissions-Policy header forbids the sensors, so
    // the events can never arrive — see _initGyro)
    this._gyroReady = false;
    this.gyroState = 'unsupported';
    this._gyroPolicyBlocked = false;   // set in _initGyro, sticky for the session
    this._rawAlpha = 0;
    this._rawBeta = 90;     // upright default
    this._rawGamma = 0;
    this._initialAlpha = null;
    this._yawOffset = 0;    // accumulated from virtual joystick X (+ drag)
    this._pitchOffset = 0;  // drag-look pitch — used only without a gyro
    this._screenOrient = 0;

    // compass fusion — see _feedAbsolute(). _corr is the filtered offset from
    // the relative alpha stream to true (magnetic) north, in degrees; null
    // until the first absolute sample, which is what compassLive reports.
    this._corr = null;
    this._relSeen = false;  // a plain `deviceorientation` event has arrived
    this._headingCorr = 0;  // constant magnetic → grid correction (setHeadingCorrection)

    this.zoomFrac = 0;      // slider position 0..1 = ln Z / ln Zmax (main.js maps it)
    this._zoomTouching = false;
    this._zoomText = '';
    this._uiKey = '';
    this._lockKey = '';
    this._gpsToggle = false; // 📍 button tapped, waiting for consumeGpsToggle
    this._gpsKey = '';       // last rendered GPS label (per-frame cheapness)

    // screen wake lock — see wakeAcquire()
    this._wake = null;         // the live WakeLockSentinel, or null
    this._wakeWanted = false;  // mobile mode is up and wants the screen on
    this.wakeState = 'idle';

    const dom = createOverlay();
    this._overlay = dom.el;
    this._lockBtn = dom.lock;
    this._keys = mountHints(document.getElementById('mob-keys'));
    this._initStick();
    this._initButtons();
    this._initZoom();
    this._initLock();
    this._initGyro();
    if (lookEl) this._initDragLook(lookEl);
    // ONE listener for the session: the browser releases the wake lock every
    // time the page hides (a call, an app switch, the screen locking anyway),
    // so every return to visibility has to take a fresh one. A no-op unless
    // mobile mode is up.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this._wakeWanted) this.wakeAcquire();
    });
  }

  get gyroLive() { return this._gyroReady; }

  // True once a compass-referenced sample has been fused: the camera yaw is
  // then the REAL direction the phone points, in grid terms. Everything that
  // would rotate the view (stick X, drag yaw, recenter) stands down while
  // this holds — see turnRate / _initDragLook / recenter.
  get compassLive() { return this._corr !== null; }

  // Degrees to add to a magnetic heading to get a grid heading in this world
  // (headingCorrectionDeg(meta) — declination minus grid convergence).
  setHeadingCorrection(deg) {
    this._headingCorr = Number.isFinite(deg) ? deg : 0;
  }

  // ---- touches on the render canvas.
  // One finger drags the look. Without a gyro this IS the look control (yaw +
  // pitch); with one it nudges yaw only — pitch comes from the sensor and a
  // mixed offset would fight it. With a live compass, or the 🔒 on, it does
  // nothing: the view is pinned. The drag follows the zoom (× lookScale).
  // Two fingers pinch the zoom (consumePinch) and never look. A tap does nothing here: inspect is the ◎ button.
  _initDragLook(el) {
    const pts = new Map();                 // identifier → { x, y }
    let primary = null, pinchD = 0;
    const K = 0.006;                       // rad per CSS px at 1× (~0.34°)
    const two = () => {
      const it = pts.values();
      const a = it.next().value, b = it.next().value;
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    el.style.touchAction = 'none';         // no browser pan/zoom on the canvas
    // a tap's synthesized click would reach FlatControls' pointer-lock
    // request (meaningless on a phone) — swallow it while touch UI is up
    el.addEventListener('click', (e) => {
      if (this._overlay.style.display !== 'none') e.stopImmediatePropagation();
    }, true);
    el.addEventListener('touchstart', (e) => {
      for (const t of e.changedTouches) pts.set(t.identifier, { x: t.clientX, y: t.clientY });
      if (pts.size === 1) {
        primary = e.changedTouches[0].identifier;
      } else {
        pinchD = two();
      }
    }, { passive: true });
    el.addEventListener('touchmove', (e) => {
      e.preventDefault();                  // still swallow the browser pan
      for (const t of e.changedTouches) {
        const p = pts.get(t.identifier);
        if (!p) continue;
        if (pts.size === 1 && t.identifier === primary) {
          const dx = t.clientX - p.x, dy = t.clientY - p.y;
          if (!this.compassLive && !this.viewLock) {
            const k = K * this.lookScale;
            this._yawOffset -= dx * k;
            if (!this._gyroReady) {
              this._pitchOffset = Math.max(-1.4, Math.min(1.4, this._pitchOffset - dy * k));
            }
          }
        }
        p.x = t.clientX; p.y = t.clientY;
      }
      if (pts.size >= 2) {
        const d = two();
        if (pinchD > 0 && d > 0) this._pinch *= d / pinchD;
        pinchD = d;
      }
    }, { passive: false });
    const end = (e) => {
      for (const t of e.changedTouches) {
        if (!pts.has(t.identifier)) continue;
        pts.delete(t.identifier);
      }
      if (pts.size < 2) pinchD = 0;
      if (pts.size === 0) primary = null;
      else if (!pts.has(primary)) primary = pts.keys().next().value;   // the drag goes on
    };
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
  }

  // ---- context-aware button visibility, driven from main.js each frame
  // (keyed, cheap): FIRE only while a gun is in hand in combat, ⟳ in combat.
  // mapOpen flips the no-gyro 🗺 button's look.
  setUi({ combat, weapon, mapOpen = false }) {
    const key = `${combat}|${weapon}`;
    if (key !== this._uiKey) {
      this._uiKey = key;
      const el = (id) => document.getElementById(id);
      el('mob-fire').style.display = combat && weapon && weapon !== 'monocular' ? '' : 'none';
      el('mob-weapon').style.display = combat ? '' : 'none';
      // the inspect button + its dot / label: combat off only (a card never opens in combat)
      for (const id of ['mob-insp', 'mob-insp-dot', 'mob-insp-lbl']) el(id).style.display = combat ? 'none' : '';
      if (!(combat && weapon && weapon !== 'monocular') && this.fireHeld) {
        this.fireHeld = false;
        el('mob-fire').classList.remove('held');
      }
    }
    const lk = `${this._gyroReady}|${this.viewLock}|${mapOpen}`;
    if (lk !== this._lockKey) {
      this._lockKey = lk;
      const b = this._lockBtn;
      if (this._gyroReady) {
        b.textContent = this.viewLock ? '🔒' : '🔓';
        b.setAttribute('aria-label', this.viewLock ? t('player.mob.unlockView') : t('player.mob.lockView'));
        b.classList.toggle('on', this.viewLock);
      } else {
        b.textContent = mapOpen ? '×' : '🗺';
        b.setAttribute('aria-label', mapOpen ? t('player.mob.closeMap') : t('player.mob.map'));
        b.classList.toggle('on', mapOpen);
      }
    }
  }

  // The inspect button's state, from Inspect.probe() (or null): lit + the
  // icon of what a tap would open + the short label near the reticle. Touches
  // classes / text only — setUi owns the display of all three elements.
  setInspect(hint) {
    const lit = !!(hint && hint.lit);
    const key = lit ? `${hint.icon}|${hint.label}` : '';
    if (key === this._inspHint) return;
    this._inspHint = key;
    const el = (id) => document.getElementById(id);
    el('mob-insp').classList.toggle('lit', lit);
    el('mob-insp-dot').classList.toggle('lit', lit);
    el('mob-insp-ic').textContent = lit ? hint.icon : '◎';
    el('mob-insp-lbl').textContent = lit ? hint.label : '';
    el('mob-insp-lbl').style.visibility = lit ? 'visible' : 'hidden';
  }

  // the slider readout: frac = ln Z / ln Zmax of the target, text = "3.2×"
  // (the finger owns the thumb while it is on the slider)
  setZoom(frac, text) {
    if (typeof frac === 'number' && !this._zoomTouching) this._setZoomFrac(frac);
    if (typeof text === 'string' && text !== this._zoomText) {
      this._zoomText = text;
      const val = document.getElementById('mob-zoom-val');
      if (val) val.textContent = text;
    }
  }

  get zoomTouching() { return this._zoomTouching; }

  // ---- the 🔒 view lock (🗺 Map toggle without orientation data)
  _initLock() {
    this._lockBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!this._gyroReady) { this._mapToggle = true; return; }
      this.viewLock = !this.viewLock;
      if (!this.viewLock) this._rebase = true;
    }, { passive: false });
  }

  // screen facing the sky (the device normal's up component), from the raw
  // sensor; false without orientation data. The tilt-to-map switch needs it
  // so lying on your back with the phone overhead never opens the Map.
  get faceUp() {
    if (!this._gyroReady) return false;
    return Math.cos(this._rawBeta * _D2R) * Math.cos(this._rawGamma * _D2R) > 0.25;
  }

  // ---- vertical zoom slider (top = zoom in)
  _initZoom() {
    const wrap = document.getElementById('mob-zoom');
    const track = document.getElementById('mob-zoom-track');
    const apply = (clientY) => {
      const r = track.getBoundingClientRect();
      this._setZoomFrac(Math.max(0, Math.min(1, 1 - (clientY - r.top) / r.height)));
    };
    let lastDown = -1e9;
    wrap.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const now = performance.now();
      if (now - lastDown < DOUBLE_TAP_MS) {          // double tap → 1×
        lastDown = -1e9;
        this._zoomReset = true;
        this._setZoomFrac(0);
        return;
      }
      lastDown = now;
      this._zoomTouching = true;
      apply(e.changedTouches[0].clientY);
    }, { passive: false });
    wrap.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (this._zoomTouching) apply(e.changedTouches[0].clientY);
    }, { passive: false });
    const end = () => { this._zoomTouching = false; };
    wrap.addEventListener('touchend', end);
    wrap.addEventListener('touchcancel', end);
  }

  _setZoomFrac(f) {
    if (f === this.zoomFrac) return;   // called every frame from setZoom
    this.zoomFrac = f;
    const fill = document.getElementById('mob-zoom-fill');
    const thumb = document.getElementById('mob-zoom-thumb');
    if (fill) fill.style.height = `${f * 100}%`;
    if (thumb) thumb.style.bottom = `${f * 100}%`;
  }

  // ---- virtual joystick (canvas touch)
  _initStick() {
    const canvas = document.getElementById('mob-stick');
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2, R = W / 2 - 10;
    let activeId = null;

    drawStick(ctx, cx, cy, R, 0, 0);

    const update = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier !== activeId) continue;
        const rect = canvas.getBoundingClientRect();
        const px = (t.clientX - rect.left) / rect.width * W;
        const py = (t.clientY - rect.top) / rect.height * H;
        let dx = (px - cx) / R, dy = (py - cy) / R;
        const mag = Math.hypot(dx, dy);
        if (mag > 1) { dx /= mag; dy /= mag; }
        this._stickX = dx;
        this._stickY = dy;
        drawStick(ctx, cx, cy, R, dx, dy);
      }
    };
    const end = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier !== activeId) continue;
        activeId = null;
        this._stickX = this._stickY = 0;
        drawStick(ctx, cx, cy, R, 0, 0);
      }
    };

    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (activeId !== null) return;
      const t = e.changedTouches[0];
      activeId = t.identifier;
      update(e);
    }, { passive: false });
    canvas.addEventListener('touchmove', (e) => { e.preventDefault(); update(e); }, { passive: false });
    canvas.addEventListener('touchend', end);
    canvas.addEventListener('touchcancel', end);
  }

  // ---- right-side action buttons
  _initButtons() {
    const fire = document.getElementById('mob-fire');
    const weapon = document.getElementById('mob-weapon');
    const jump = document.getElementById('mob-jump');

    fire.addEventListener('touchstart', (e) => {
      e.preventDefault(); this.fireHeld = true; fire.classList.add('held');
    }, { passive: false });
    fire.addEventListener('touchend', (e) => {
      e.preventDefault(); this.fireHeld = false; fire.classList.remove('held');
    }, { passive: false });
    fire.addEventListener('touchcancel', () => {
      this.fireHeld = false; fire.classList.remove('held');
    });

    weapon.addEventListener('touchstart', (e) => {
      e.preventDefault(); this._weaponNext = true;
    }, { passive: false });

    jump.addEventListener('touchstart', (e) => {
      e.preventDefault(); this._jumpQueued = true;
    }, { passive: false });

    document.getElementById('mob-insp').addEventListener('touchstart', (e) => {
      e.preventDefault(); this._inspTap = true;
    }, { passive: false });

    const gps = document.getElementById('mob-gps');
    gps.addEventListener('touchstart', (e) => {
      e.preventDefault(); this._gpsToggle = true;
    }, { passive: false });

    // Guidance card: the same toggle flag the 📍 button raises, so main.js
    // re-issues the location request from THIS touch (a request made outside
    // a gesture never opens a prompt). The card closes either way — a fix or
    // a fresh failure re-opens it with the then-current diagnosis.
    const ghRetry = document.getElementById('mob-gh-retry');
    ghRetry.addEventListener('touchstart', (e) => {
      e.preventDefault(); this._gpsToggle = true; this.hideGpsHelp();
    }, { passive: false });
    const ghX = document.getElementById('mob-gh-x');
    ghX.addEventListener('touchstart', (e) => {
      e.preventDefault(); this.hideGpsHelp();
    }, { passive: false });
  }

  // 📍 tapped since the last call? (main.js owns the GPS state machine —
  // this class only reports the tap and renders what it is told)
  consumeGpsToggle() {
    if (this._gpsToggle) { this._gpsToggle = false; return true; }
    return false;
  }

  // GPS button label + state. Called every frame, so it only touches the DOM
  // when the rendered result actually changes — innerHTML on a phone at 60 Hz
  // is a real cost. `err` is a short reason ('denied', 'no fix', …).
  setGps({ on, acc, err }) {
    const label = err ? '!'
      : !on ? t('player.mob.gpsLabel')
      : Number.isFinite(acc) ? `±${Math.round(acc)} m`
      : '…';
    const key = `${label}|${on ? 1 : 0}|${err ? 1 : 0}`;
    if (key === this._gpsKey) return;
    this._gpsKey = key;
    const el = document.getElementById('mob-gps');
    if (!el) return;
    el.innerHTML = `<span class="g-ic">📍</span><small>${label}</small>`;
    el.classList.toggle('active', !!on);
    el.classList.toggle('err', !!err);
  }

  // In-game GPS guidance card. main.js diagnoses a failed location request
  // (gpsDiagnose) and hands the result here whenever it happens in mobile
  // mode: fullscreen hides the browser chrome, so the landing page's panel is
  // unreachable and a silent 📍 "!" is all the player would otherwise get.
  // `cause` is optional and only steers the extra note.
  showGpsHelp({ title, steps, tech, cause } = {}) {
    const card = document.getElementById('mob-gpshelp');
    if (!card) return;
    document.getElementById('mob-gh-title').textContent = title || t('player.mob.gps.unavailable');
    // The address-bar steps below cannot be reached from fullscreen — say so,
    // or the player taps the padlock that is not on screen and gives up.
    // …and a header block is not reachable from anywhere on this phone at all,
    // so say that too instead of letting the player hunt for a setting.
    document.getElementById('mob-gh-note').textContent = cause === 'blocked'
      ? t('player.mob.gps.noteBlocked')
      : cause === 'policy'
        ? t('player.mob.gps.notePolicy')
        : '';
    const ol = document.getElementById('mob-gh-steps');
    ol.textContent = '';
    for (const s of (steps || [])) {
      const li = document.createElement('li');
      li.textContent = s;        // never innerHTML: some of this is browser text
      ol.appendChild(li);
    }
    document.getElementById('mob-gh-tech').textContent = tech || '';
    card.classList.add('show');
  }

  hideGpsHelp() {
    const card = document.getElementById('mob-gpshelp');
    if (card) card.classList.remove('show');
  }

  // ---- device orientation → quaternion (same math as phone WebXR)
  _initGyro() {
    // the screen's rotation, in deviceorientation's frame (counter-clockwise,
    // = window.orientation). iOS Safari (16.4+) has screen.orientation too,
    // but its angle runs CLOCKWISE (90 where window.orientation says -90, a
    // landscape view rolled 180°) and lags a turn during orientationchange —
    // so window.orientation wins wherever it exists (iOS, Android: the same
    // value mod 360 there); screen.orientation.angle only without it
    const updateOrient = () => {
      const deg = typeof window.orientation === 'number' ? window.orientation
        : screen.orientation ? screen.orientation.angle : 0;
      this._screenOrient = deg * _D2R;
    };
    updateOrient();
    window.addEventListener('orientationchange', updateOrient);
    if (screen.orientation) screen.orientation.addEventListener('change', updateOrient);

    // `abs` = this event carries a compass-referenced alpha. Two browsers,
    // two spellings: Android Chrome fires a separate `deviceorientationabsolute`
    // (and sometimes a plain `deviceorientation` with absolute === true),
    // iOS Safari hangs webkitCompassHeading off the ordinary event.
    const handler = (e, abs) => {
      // a device with no usable sensor fires one all-null event: ignore it.
      // alpha alone may be null (no gyro/magnetometer, accelerometer only):
      // tilt still works, heading comes from the stick / drag — the old
      // `alpha === null → return` froze the whole view on such phones
      if (e.alpha === null && e.beta === null && e.gamma === null) return;
      const a = e.alpha === null ? 0 : e.alpha;
      if (this._initialAlpha === null) this._initialAlpha = a;
      if (!this._gyroReady) {
        this._gyroReady = true;
        this.gyroState = 'live';
      }
      if (!abs) {
        // the relative stream is the MOTION source: it is smooth and fast,
        // where the compass is slow and jittery
        if (!this._relSeen) {
          this._relSeen = true;
          // drop any correction seeded while we thought this phone was
          // absolute-only — the next absolute sample re-seeds it against the
          // relative stream, which is what _corr is defined against
          this._corr = null;
        }
        this._rawAlpha = a;
        if (e.beta !== null) this._rawBeta = e.beta;
        if (e.gamma !== null) this._rawGamma = e.gamma;
      } else {
        // an absolute-only phone (no plain deviceorientation at all) still
        // needs tilt from somewhere — take it from this event
        if (!this._relSeen) {
          if (e.beta !== null) this._rawBeta = e.beta;
          if (e.gamma !== null) this._rawGamma = e.gamma;
        }
      }
      // compass-referenced alpha, whichever spelling delivered it
      const ch = e.webkitCompassHeading;
      if (typeof ch === 'number' && Number.isFinite(ch)) {
        // iOS reports degrees CLOCKWISE from magnetic north; alpha runs the
        // other way, so the two are mirror images of each other
        this._feedAbsolute((360 - ch) % 360);
      } else if ((abs || e.absolute === true) && e.alpha !== null) {
        this._feedAbsolute(e.alpha);
      }
    };

    if (typeof DeviceOrientationEvent === 'undefined') return;
    this._orientHandler = (e) => handler(e, false);
    this._absHandler = (e) => handler(e, true);
    // A `permissions-policy: gyroscope=(), magnetometer=()` header stops the
    // events at the source: the listeners below attach without complaint and
    // then never fire, which used to read as 'waiting' FOREVER — the phone
    // looked broken and the briefing kept promising motion data that could
    // not come. Say it out loud instead. The listeners stay armed anyway, so
    // if the verdict is ever wrong the first real event still flips the state
    // to 'live'.
    this._gyroPolicyBlocked = policyAllows('gyroscope') === false
      || policyAllows('magnetometer') === false;
    // `deviceorientationabsolute` is Chromium-only and simply never fires
    // elsewhere — registering it unconditionally costs nothing
    window.addEventListener('deviceorientationabsolute', this._absHandler);
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      this._needsPermission = true;
      this.gyroState = this._gyroPolicyBlocked ? 'policy' : 'permission';
    } else {
      window.addEventListener('deviceorientation', this._orientHandler);
      this.gyroState = this._gyroPolicyBlocked ? 'policy' : 'waiting';
    }
  }

  // Fuse one compass-referenced alpha into _corr, the offset that turns the
  // relative stream into a true heading. A complementary filter: the compass
  // pulls slowly (3 % per sample) so its jitter and its ±15° magnetic slop
  // never shake the view, while the gyro carries every fast head turn.
  _feedAbsolute(absAlpha) {
    if (!Number.isFinite(absAlpha)) return;
    if (!this._relSeen) {
      // no relative stream on this phone: the absolute alpha IS the motion
      // source, and no correction is needed on top of it
      this._rawAlpha = absAlpha;
      this._corr = 0;
      return;
    }
    const d = wrap180(absAlpha - this._rawAlpha);
    // first sample snaps (starting 180° wrong and creeping there at 3 % a
    // frame would be a minute of visibly wrong world)
    this._corr = this._corr === null ? d : wrap180(this._corr + wrap180(d - this._corr) * 0.03);
  }

  // Entering mobile mode: the direction the phone points RIGHT NOW becomes
  // the spawn heading (main.js seeds _yawOffset = player.yaw first). The old
  // calibration used the very first event — fired at page load, phone lying
  // wherever — so the start heading was arbitrary.
  // With a live compass there is nothing to calibrate — the heading is the
  // real one — and re-zeroing alpha here would make the view jump on every
  // recenter() (show(), the briefing's GO button) away from true north.
  recenter() {
    if (this._gyroReady && !this.compassLive) this._initialAlpha = this._rawAlpha;
    this._pitchOffset = 0;
  }

  // Apply the device orientation to the camera as a quaternion — called
  // each frame from tick. Combines the raw sensor, the initial calibration
  // (so the start direction matches the game's spawn heading), and the
  // joystick yaw offset. Without sensor data the camera is driven by the
  // stick / drag offsets alone, so the view is never frozen.
  updateCamera(camera) {
    if (this.viewLock) return;               // 🔒: the camera stays put
    if (!this._gyroReady) {
      camera.rotation.set(this._pitchOffset, this._yawOffset, 0, 'YXZ');
      return;
    }
    // Compass mode is ABSOLUTE: game coords are geo-true (−z = grid north)
    // and an absolute alpha of 0 already means "top of the phone points
    // north", so the heading needs no calibration offset and no stick yaw —
    // only the constant magnetic → grid correction. Mixing either in would
    // decouple the view from the real world, which is the whole point here.
    const alpha = this.compassLive
      ? (this._rawAlpha + this._corr + this._headingCorr) * _D2R
      : (this._rawAlpha - this._initialAlpha) * _D2R + this._yawOffset;
    const beta  = this._rawBeta * _D2R;
    const gamma = this._rawGamma * _D2R;

    _euler.set(beta, alpha, -gamma, 'YXZ');
    _dq.setFromEuler(_euler);
    _dq.multiply(_Q1);
    _dq.multiply(_q0.setFromAxisAngle(_zee, -this._screenOrient));
    // leaving the 🔒: continue from the frozen heading (the relative stream
    // only — a live compass is absolute and has to jump back to true)
    if (this._rebase) {
      this._rebase = false;
      if (!this.compassLive) {
        const d = _yawOf(camera.quaternion) - _yawOf(_dq);
        const dy = Math.atan2(Math.sin(d), Math.cos(d));
        this._yawOffset += dy;
        _dq.premultiply(_q0.setFromAxisAngle(_yUp, dy));
      }
    }
    camera.quaternion.copy(_dq);
  }

  // iOS 13+: must be called synchronously inside a user gesture (no await
  // before it on the caller's path). A refusal is remembered per site by
  // Safari, so 'denied' is sticky until the user resets the site's settings.
  async requestGyroPermission() {
    if (!this._needsPermission) return true;
    try {
      const resp = await DeviceOrientationEvent.requestPermission();
      if (resp === 'granted') {
        window.addEventListener('deviceorientation', this._orientHandler);
        // re-arm the absolute stream too: a Chromium-based iOS browser gates
        // it behind the same prompt, and on Safari it simply never fires
        window.addEventListener('deviceorientationabsolute', this._absHandler);
        this._needsPermission = false;
        // a granted prompt still cannot outrank the site's own header: the
        // events remain impossible, so keep saying so rather than 'waiting'
        this.gyroState = this._gyroPolicyBlocked ? 'policy' : 'waiting';
        return true;
      }
    } catch (_) { /* denied */ }
    this.gyroState = 'denied';
    return false;
  }

  show() {
    this._overlay.style.display = 'block';
    this._lockBtn.style.display = 'flex';
    document.body.classList.add('mob');
    this.recenter();
    this.wakeAcquire();
  }
  hide() {
    this._overlay.style.display = 'none';
    this._lockBtn.style.display = 'none';
    document.body.classList.remove('mob');
    this.viewLock = false;
    this._lockKey = '';
    this.fireHeld = false;
    this._keys.set([]);          // the hint cycle stops with the overlay
    this.wakeRelease();
  }

  // ------------------------------------------------------- screen wake lock
  // A phone dims and locks on its own timer, and a WebGL scene nobody is
  // touching does not count as activity: the display goes out mid-flight,
  // which in GPS-follow mode is exactly when the player is walking and not
  // looking at the screen. The Screen Wake Lock API is the only thing that
  // holds it on; fullscreen does NOT. Idempotent — safe to call on every
  // entry and every return to visibility.
  // wakeState: 'idle' (not wanted) | 'held' | 'unsupported' (no API) |
  // 'policy' (this page's Permissions-Policy forbids screen-wake-lock, so the
  // request can never succeed) | 'failed' (refused — battery saver is the
  // common cause). Only the non-'held' outcomes are worth telling the player
  // about: the briefing reads this.
  async wakeAcquire() {
    this._wakeWanted = true;
    if (this._wake) return;                                  // already holding one
    if (!('wakeLock' in navigator)) { this.wakeState = 'unsupported'; return; }
    // same reasoning as the sensors: an empty allowlist in the page's own
    // header switches the feature off for the page itself, and no permission
    // prompt or phone setting can lift it — say so instead of failing mutely
    if (policyAllows('screen-wake-lock') === false) { this.wakeState = 'policy'; return; }
    if (document.visibilityState !== 'visible') return;      // the retake will do it
    try {
      const lock = await navigator.wakeLock.request('screen');
      if (!this._wakeWanted) { lock.release().catch(() => {}); return; }  // left mobile mid-request
      this._wake = lock;
      this.wakeState = 'held';
      // the browser drops the lock on its own (page hidden, battery saver
      // kicking in): forget it so the next visibility change asks again
      lock.addEventListener('release', () => { if (this._wake === lock) this._wake = null; });
    } catch (e) {
      this.wakeState = 'failed';                             // soft: the scene runs, the screen sleeps
    }
  }

  wakeRelease() {
    this._wakeWanted = false;
    this.wakeState = 'idle';
    const lock = this._wake;
    this._wake = null;
    if (lock) lock.release().catch(() => {});
  }

  // Walk vector from the stick in a given yaw direction (yaw extracted from
  // the camera quaternion in main.js, not stored here).
  // Two grammars: without a compass X turns the body, so only Y walks; with
  // one the view is owned by the phone and X has nothing left to steer — it
  // becomes strafe, making the stick a full 2-D pad (right = +).
  moveDirWithYaw(out, yaw) {
    let sy = this._stickY;
    if (Math.abs(sy) < _STICK_DZ) sy = 0;
    const sin = Math.sin(yaw), cos = Math.cos(yaw);
    let sx = 0;
    if (this.compassLive) {
      sx = this._stickX;
      if (Math.abs(sx) < _STICK_DZ) sx = 0;
    }
    // forward (sy = −1) is (−sin, −cos); its right-hand side is (cos, −sin)
    out.set(sin * sy + cos * sx, 0, cos * sy - sin * sx);
    if (out.lengthSq() > 1) out.normalize();
    return out;
  }

  // stick X drives yaw turn (called in tick, returns radians/frame delta).
  // Zero in compass mode — there X strafes and the heading is the phone's.
  get turnRate() {
    if (this.compassLive || this.viewLock) return 0;
    return Math.abs(this._stickX) > _STICK_DZ ? this._stickX : 0;
  }

  // The player is driving. GPS follow pauses while this holds, so releasing
  // the stick hands control back to the real-world position.
  get stickActive() {
    return Math.abs(this._stickX) > _STICK_DZ || Math.abs(this._stickY) > _STICK_DZ;
  }

  get sprint() { return Math.hypot(this._stickX, this._stickY) > 0.9; }

  get jump() {
    if (this._jumpQueued) { this._jumpQueued = false; return true; }
    return false;
  }

  consumeWeaponNext() {
    if (this._weaponNext) { this._weaponNext = false; return true; }
    return false;
  }

  // the ◎ inspect button tapped since the last call (the phone's LMB; there
  // is no centre tap)
  consumeInspectTap() {
    if (this._inspTap) { this._inspTap = false; return true; }
    return false;
  }

  // the pinch scale since the last call (> 1 = fingers spread = zoom in)
  consumePinch() {
    const p = this._pinch;
    this._pinch = 1;
    return p;
  }

  consumeZoomReset() {
    if (this._zoomReset) { this._zoomReset = false; return true; }
    return false;
  }

  // 🗺 tapped (the lock button on a phone without orientation data)
  // the key-hint pages (main.js, the HUD cadence; plain text): written only
  // when the set changes, cycled + cross-faded by keyhints.js mountHints
  setKeyHints(pages) {
    this._keys.set(pages.map((p) => p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')));
  }

  consumeMapToggle() {
    if (this._mapToggle) { this._mapToggle = false; return true; }
    return false;
  }
}

// --------------------------------------------------------------- detection
export function isMobileDevice() {
  return 'ontouchstart' in window
    && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
